import { create } from "zustand";

/**
 * Per-view layout variant for the schedule surfaces being A/B-compared during
 * the redesign ("next" = new design-system layout, "classic" = the previous
 * one). Keyed by view name ("timeline", "grid", …) so each view can be
 * compared independently. The comparison window is temporary — once a winner
 * is picked the losing layout and this switch are removed.
 */
export type LayoutVariant = "next" | "classic";

interface PreferencesState {
  hideProduction: boolean;
  productionInfoSeen: boolean;
  showOrgBanner: boolean;
  layoutVariants: Record<string, LayoutVariant>;
  festivalTabs: Record<string, string>;
  setHideProduction: (hide: boolean) => void;
  setProductionInfoSeen: () => void;
  setShowOrgBanner: (show: boolean) => void;
  setLayoutVariant: (view: string, variant: LayoutVariant) => void;
  setFestivalTab: (festivalId: string, tab: string) => void;
}

interface Persisted {
  hideProduction: boolean;
  productionInfoSeen: boolean;
  showOrgBanner: boolean;
  layoutVariants: Record<string, LayoutVariant>;
  festivalTabs: Record<string, string>;
}

// Last-viewed tab per festival ("console", "crew", …) so returning to a
// festival lands where the user left off. Capped so the map cannot grow
// unbounded across many festivals; insertion order doubles as age.
const MAX_FESTIVAL_TABS = 40;

function sanitizeFestivalTabs(raw: unknown): Record<string, string> {
  if (typeof raw !== "object" || raw === null) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "string" && value) out[key] = value;
  }
  return out;
}

function capFestivalTabs(tabs: Record<string, string>): Record<string, string> {
  const keys = Object.keys(tabs);
  if (keys.length <= MAX_FESTIVAL_TABS) return tabs;
  const trimmed = { ...tabs };
  for (const key of keys.slice(0, keys.length - MAX_FESTIVAL_TABS)) {
    delete trimmed[key];
  }
  return trimmed;
}

function sanitizeLayoutVariants(raw: unknown): Record<string, LayoutVariant> {
  if (typeof raw !== "object" || raw === null) return {};
  const out: Record<string, LayoutVariant> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === "next" || value === "classic") out[key] = value;
  }
  return out;
}

function load(): Persisted {
  try {
    const raw = localStorage.getItem("trace_preferences");
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        hideProduction: !!parsed.hideProduction,
        productionInfoSeen: !!parsed.productionInfoSeen,
        // Default true (banner shown) when the key is missing so users who
        // upgrade see the new banner without opting in.
        showOrgBanner:
          parsed.showOrgBanner === undefined ? true : !!parsed.showOrgBanner,
        layoutVariants: sanitizeLayoutVariants(parsed.layoutVariants),
        festivalTabs: sanitizeFestivalTabs(parsed.festivalTabs),
      };
    }
  } catch {
    // ignore
  }
  return {
    hideProduction: false,
    productionInfoSeen: false,
    showOrgBanner: true,
    layoutVariants: {},
    festivalTabs: {},
  };
}

function save(state: Persisted) {
  localStorage.setItem("trace_preferences", JSON.stringify(state));
}

const initial = load();

function persisted(get: () => PreferencesState): Persisted {
  const s = get();
  return {
    hideProduction: s.hideProduction,
    productionInfoSeen: s.productionInfoSeen,
    showOrgBanner: s.showOrgBanner,
    layoutVariants: s.layoutVariants,
    festivalTabs: s.festivalTabs,
  };
}

export const usePreferencesStore = create<PreferencesState>((set, get) => ({
  hideProduction: initial.hideProduction,
  productionInfoSeen: initial.productionInfoSeen,
  showOrgBanner: initial.showOrgBanner,
  layoutVariants: initial.layoutVariants,
  festivalTabs: initial.festivalTabs,
  setHideProduction: (hide: boolean) => {
    save({ ...persisted(get), hideProduction: hide });
    set({ hideProduction: hide });
  },
  setProductionInfoSeen: () => {
    save({ ...persisted(get), productionInfoSeen: true });
    set({ productionInfoSeen: true });
  },
  setShowOrgBanner: (show: boolean) => {
    save({ ...persisted(get), showOrgBanner: show });
    set({ showOrgBanner: show });
  },
  setLayoutVariant: (view: string, variant: LayoutVariant) => {
    const layoutVariants = { ...get().layoutVariants, [view]: variant };
    save({ ...persisted(get), layoutVariants });
    set({ layoutVariants });
  },
  setFestivalTab: (festivalId: string, tab: string) => {
    if (!festivalId || !tab) return;
    const current = get().festivalTabs;
    if (current[festivalId] === tab) return;
    // Re-insert at the end so the cap always drops the least recent festival.
    const { [festivalId]: _previous, ...rest } = current;
    const festivalTabs = capFestivalTabs({ ...rest, [festivalId]: tab });
    save({ ...persisted(get), festivalTabs });
    set({ festivalTabs });
  },
}));
